/**
 * Schedule Classes Service
 *
 * Centralized service for managing scheduled classes data fetching.
 * This service handles:
 * - Fetching user's allowed courses from user_gym_assignments
 * - Intelligent caching with TTL and invalidation
 * - Centralized logging for debugging
 * - Consistent data fetching across the application
 *
 * @example
 * ```typescript
 * // Fetch classes with automatic user context
 * const classes = await scheduleClassesService.fetchClassesWithUserContext(startDate, endDate);
 *
 * // Invalidate cache after updating allowed courses
 * scheduleClassesService.invalidateCache(userId, gymId);
 * ```
 */

import { supabase } from "@/integrations/supabase/client";
import { ScheduledClass } from "@/types/types";
import { fetchScheduledClasses } from "@/store/schedule/useScheduledClasses";
import { formatDateForDatabase } from "@/utils/dateUtils";

interface CacheEntry {
  data: ScheduledClass[];
  timestamp: number;
  allowedCourses: string[] | null;
}

class ScheduleClassesService {
  // Cache for fetched classes
  private classesCache: Map<string, CacheEntry> = new Map();

  // Cache for user's allowed courses
  private allowedCoursesCache: Map<string, string[] | null> = new Map();

  // Cache TTL (Time To Live) - 1 minute
  private readonly CACHE_TTL_MS = 60000;

  /**
   * Generate cache key for classes data
   */
  private getClassesCacheKey(
    userId: string,
    gymId: string,
    startDate?: Date,
    endDate?: Date,
    calendarId?: string | null
  ): string {
    const start = startDate ? formatDateForDatabase(startDate) : "all";
    const end = endDate ? formatDateForDatabase(endDate) : "all";
    const cal = calendarId ?? "all";
    return `${userId}-${gymId}-${start}-${end}-${cal}`;
  }

  /**
   * Generate cache key for allowed courses
   */
  private getAllowedCoursesCacheKey(userId: string, gymId: string): string {
    return `${userId}-${gymId}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const entry = this.classesCache.get(cacheKey);
    if (!entry) return false;

    const age = Date.now() - entry.timestamp;
    return age < this.CACHE_TTL_MS;
  }

  /**
   * Fetch user's allowed courses from database
   * Uses cache to avoid repeated queries
   *
   * @param userId - User ID
   * @param gymId - Gym ID
   * @returns Array of allowed course IDs, or null if all courses are allowed
   */
  async getUserAllowedCourses(userId: string, gymId: string): Promise<string[] | null> {
    const cacheKey = this.getAllowedCoursesCacheKey(userId, gymId);

    // Return cached value if available
    if (this.allowedCoursesCache.has(cacheKey)) {
      const cached = this.allowedCoursesCache.get(cacheKey);
      console.log(`[ScheduleClassesService] Using cached allowed_courses for user ${userId}, gym ${gymId}:`, cached);
      return cached!;
    }

    try {
      console.log(`[ScheduleClassesService] Fetching allowed_courses for user ${userId}, gym ${gymId}`);

      const { data, error } = await supabase
        .from("user_gym_assignments")
        .select("allowed_courses")
        .eq("user_id", userId)
        .eq("gym_id", gymId)
        .single();

      if (error) {
        console.error("[ScheduleClassesService] Error fetching allowed courses:", error);
        // Return null (all courses allowed) on error
        this.allowedCoursesCache.set(cacheKey, null);
        return null;
      }

      const allowedCourses = (data?.allowed_courses as string[] | null) || null;
      console.log(`[ScheduleClassesService] Fetched allowed_courses:`, allowedCourses);

      // Cache the result
      this.allowedCoursesCache.set(cacheKey, allowedCourses);

      return allowedCourses;
    } catch (error) {
      console.error("[ScheduleClassesService] Exception fetching allowed courses:", error);
      // Return null (all courses allowed) on exception
      this.allowedCoursesCache.set(cacheKey, null);
      return null;
    }
  }

  /**
   * Fetch scheduled classes with automatic user context
   * This is the main method used by components
   *
   * @param userId - User ID
   * @param gymId - Gym ID
   * @param startDate - Start date (optional)
   * @param endDate - End date (optional)
   * @param forceRefresh - Force refresh bypassing cache
   * @param calendarId - Optional filter by calendar
   * @returns Array of scheduled classes
   */
  async fetchClassesWithUserContext(
    userId: string,
    gymId: string,
    startDate?: Date,
    endDate?: Date,
    forceRefresh: boolean = false,
    calendarId?: string | null
  ): Promise<ScheduledClass[]> {
    const cacheKey = this.getClassesCacheKey(userId, gymId, startDate, endDate, calendarId);

    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.classesCache.get(cacheKey);
      console.log(`[ScheduleClassesService] Using cached classes (${cached!.data.length} items)`);
      return cached!.data;
    }

    console.log(`[ScheduleClassesService] Fetching classes from server`, {
      userId,
      gymId,
      startDate: startDate ? formatDateForDatabase(startDate) : undefined,
      endDate: endDate ? formatDateForDatabase(endDate) : undefined,
      forceRefresh,
      calendarId,
    });

    const allowedCourses = await this.getUserAllowedCourses(userId, gymId);

    const formattedStartDate = startDate ? formatDateForDatabase(startDate) : undefined;
    const formattedEndDate = endDate ? formatDateForDatabase(endDate) : undefined;

    try {
      const classes = await fetchScheduledClasses(
        formattedStartDate,
        formattedEndDate,
        allowedCourses,
        calendarId
      );
      
      console.log('[ScheduleClassesService] Classes returned from helper:', {
        count: classes.length,
        first: classes[0]
      });
      
      if (!classes || classes.length === 0) {
        console.warn('[ScheduleClassesService] No classes returned from edge function');
      }

      console.log(`[ScheduleClassesService] Fetched ${classes.length} classes from server`);

      // Update cache
      this.classesCache.set(cacheKey, {
        data: classes,
        timestamp: Date.now(),
        allowedCourses,
      });

      return classes;
    } catch (error) {
      console.error('[ScheduleClassesService] Error in fetchScheduledClasses:', error);
      throw error;
    }
  }

  /**
   * Refresh classes (convenience method)
   * Equivalent to fetchClassesWithUserContext with forceRefresh=true
   */
  async refreshClasses(
    userId: string,
    gymId: string,
    startDate?: Date,
    endDate?: Date,
    calendarId?: string | null
  ): Promise<ScheduledClass[]> {
    return this.fetchClassesWithUserContext(
      userId,
      gymId,
      startDate,
      endDate,
      true,
      calendarId
    );
  }

  /**
   * Invalidate all caches for a specific user/gym
   * Call this when allowed_courses are updated
   *
   * @param userId - User ID
   * @param gymId - Gym ID
   */
  invalidateCache(userId: string, gymId: string): void {
    console.log(`[ScheduleClassesService] Invalidating cache for user ${userId}, gym ${gymId}`);

    // Remove allowed courses cache
    const allowedCoursesKey = this.getAllowedCoursesCacheKey(userId, gymId);
    this.allowedCoursesCache.delete(allowedCoursesKey);

    // Remove all classes cache entries for this user/gym
    const keysToDelete: string[] = [];
    this.classesCache.forEach((_, key) => {
      if (key.startsWith(`${userId}-${gymId}-`)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.classesCache.delete(key));

    console.log(`[ScheduleClassesService] Invalidated ${keysToDelete.length} cache entries`);
  }

  /**
   * Invalidate all caches (nuclear option)
   * Use when you need to clear everything
   */
  invalidateAllCaches(): void {
    console.log("[ScheduleClassesService] Invalidating ALL caches");
    this.classesCache.clear();
    this.allowedCoursesCache.clear();
  }

  /**
   * Prefetch classes for a date range
   * Useful for preloading next/previous weeks
   *
   * @param userId - User ID
   * @param gymId - Gym ID
   * @param startDate - Start date
   * @param endDate - End date
   */
  async prefetchClasses(
    userId: string,
    gymId: string,
    startDate: Date,
    endDate: Date,
    calendarId?: string | null
  ): Promise<void> {
    const cacheKey = this.getClassesCacheKey(userId, gymId, startDate, endDate, calendarId);

    if (!this.isCacheValid(cacheKey)) {
      console.log(`[ScheduleClassesService] Prefetching classes`, {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
        calendarId,
      });

      this.fetchClassesWithUserContext(userId, gymId, startDate, endDate, false, calendarId).catch((error) => {
        console.error("[ScheduleClassesService] Prefetch error:", error);
      });
    }
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats(): {
    classesEntries: number;
    allowedCoursesEntries: number;
    validEntries: number;
  } {
    let validEntries = 0;
    this.classesCache.forEach((_, key) => {
      if (this.isCacheValid(key)) validEntries++;
    });

    return {
      classesEntries: this.classesCache.size,
      allowedCoursesEntries: this.allowedCoursesCache.size,
      validEntries,
    };
  }
}

// Export singleton instance
export const scheduleClassesService = new ScheduleClassesService();
